import React, { useContext, useState } from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Box, 
  Typography, 
  Switch, 
  Divider,
  Collapse,
  ListItemButton
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import GroupIcon from '@mui/icons-material/Group';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ShieldIcon from '@mui/icons-material/Shield';
import LogoutIcon from '@mui/icons-material/Logout';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import ReportIcon from '@mui/icons-material/Report';
import PremiumIcon from '@mui/icons-material/WorkspacePremium';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ArticleIcon from '@mui/icons-material/Article';
import BusinessIcon from '@mui/icons-material/Business';
import ToysIcon from '@mui/icons-material/Toys';
import PaymentIcon from '@mui/icons-material/Payment';
import { useNavigate, useLocation } from 'react-router-dom';
import { MODULES, CONTEXTS } from 'figshelf-modules';

const Sidebar = ({ selected, onSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(CONTEXTS.AuthContext);
  const [openSections, setOpenSections] = useState({
    management: true,
    premium: true,
    system: false,
  });

  const getUserRole = () => {
    return auth.user?.role?.toLowerCase() || 'user';
  };

  const handleSectionToggle = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSelect = (key) => {
    onSelect(key);
    if (key === 'dashboard') navigate('/');
    if (key === 'user-management') navigate('/user-management');
    if (key === 'company-management') navigate('/company-management');
    if (key === 'group-management') navigate('/group-management');
    if (key === 'figure-management') navigate('/figure-management');
    if (key === 'figure-database-management') navigate('/figure-database-management');
    if (key === 'admin-management') navigate('/admin-management');
    if (key === 'moderator-management') navigate('/moderator-management');
    if (key === 'moderator-controls') navigate('/moderator-controls');
    if (key === 'reports') navigate('/reports');
    if (key === 'wiki-approval') navigate('/wiki-approval');
    if (key === 'premium-features') navigate('/premium-features');
    if (key === 'discount-codes') navigate('/discount-codes');
    if (key === 'premium-subscribers') navigate('/premium-subscribers');
    if (key === 'payment-requests') navigate('/payment-requests');
    if (key === 'database') navigate('/database');
    if (key === 'settings') navigate('/settings');
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Check if user has permission to access a menu item
  const hasPermission = (requiredRoles) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    const userRole = getUserRole();
    return requiredRoles.includes(userRole);
  };

  const menuStructure = [
    {
      key: 'dashboard',
      path: '/',
      icon: <HomeIcon />,
      label: 'Dashboard',
      standalone: true,
      roles: ['admin', 'moderator', 'user'], // All roles can access
    },
    {
      section: 'management',
      label: 'Management',
      icon: <SupervisorAccountIcon />,
      roles: ['admin', 'moderator'], // Only admin and moderator can see this section
      items: [
        {
          key: 'user-management',
          path: '/user-management',
          icon: <GroupIcon />,
          label: 'Users',
          roles: ['admin', 'moderator'], // Both can view users
        },
        {
          key: 'company-management',
          path: '/company-management',
          icon: <BusinessIcon />,
          label: 'Companies',
          roles: ['admin', 'moderator'],
        },
        {
          key: 'group-management',
          path: '/group-management',
          icon: <GroupIcon />,
          label: 'Groups',
          roles: ['admin', 'moderator'],
        },
        {
          key: 'figure-management',
          path: '/figure-management',
          icon: <ToysIcon />,
          label: 'Figures',
          roles: ['admin', 'moderator'],
        },
        {
          key: 'figure-database-management',
          path: '/figure-database-management',
          icon: <StorageIcon />,
          label: 'Figure Database',
          roles: ['admin', 'moderator'],
        },
        {
          key: 'admin-management',
          path: '/admin-management',
          icon: <AdminPanelSettingsIcon />,
          label: 'Admins',
          roles: ['admin'], // Only admins can manage admins
        },
        {
          key: 'moderator-management',
          path: '/moderator-management',
          icon: <ShieldIcon />,
          label: 'Moderators',
          roles: ['admin'], // Only admins can manage moderators
        },
      ],
    },
    {
      key: 'moderator-controls',
      path: '/moderator-controls',
      icon: <ShieldIcon />,
      label: 'Moderator Controls',
      standalone: true,
      roles: ['admin', 'moderator'], // Only moderators and admins
    },
    {
      key: 'reports',
      path: '/reports',
      icon: <ReportIcon />,
      label: 'Reports',
      standalone: true,
      roles: ['admin', 'moderator'], // Only moderators and admins can view reports
    },
    {
      key: 'wiki-approval',
      path: '/wiki-approval',
      icon: <ArticleIcon />,
      label: 'Wiki Approval',
      standalone: true,
      roles: ['admin', 'moderator'], // Only moderators and admins can approve articles
    },
    {
      section: 'premium',
      label: 'Premium',
      icon: <PremiumIcon />,
      roles: ['admin'],
      items: [
        { key: 'premium-features', path: '/premium-features', icon: <PremiumIcon />, label: 'Assets', roles: ['admin'] },
        { key: 'discount-codes', path: '/discount-codes', icon: <LocalOfferIcon />, label: 'Discount Codes', roles: ['admin'] },
        { key: 'premium-subscribers', path: '/premium-subscribers', icon: <PeopleIcon />, label: 'Subscribers', roles: ['admin'] },
        { key: 'payment-requests', path: '/payment-requests', icon: <PaymentIcon />, label: 'Payment Requests', roles: ['admin'] },
      ],
    },
    {
      section: 'system',
      label: 'System',
      icon: <SettingsIcon />,
      roles: ['admin'], // Only admins can access system settings
      items: [
        {
          key: 'database',
          path: '/database',
          icon: <StorageIcon />,
          label: 'Database',
          roles: ['admin'],
        },
        {
          key: 'settings',
          path: '/settings',
          icon: <SettingsIcon />,
          label: 'Settings',
          roles: ['admin'],
        },
      ],
    },
  ];

  // Determine selected item based on current location
  const getSelectedKey = () => {
    const currentPath = location.pathname;
    // Check all items including nested ones
    for (const menuItem of menuStructure) {
      if (menuItem.standalone && menuItem.path === currentPath) {
        return menuItem.key;
      }
      if (menuItem.items) {
        const found = menuItem.items.find(item => item.path === currentPath);
        if (found) return found.key;
      }
    }
    return 'dashboard';
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: 250,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { width: 250, boxSizing: 'border-box', background: '#fff', borderRight: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 'auto', height: 'auto', borderRadius: 2, mr: 1 }}>
            <img src={MODULES.Resources.logos.logo_1} alt="Figshelf" style={{ width: '100%', height: '100%' }} />
          </Box>
        </Box>
        <Divider />
        <List sx={{ flex: 1, py: 1 }}>
          {menuStructure.map((menuItem) => {
            // Check role permissions for standalone items
            if (menuItem.standalone) {
              if (!hasPermission(menuItem.roles)) return null;
              
              return (
                <ListItemButton
                  key={menuItem.key}
                  selected={getSelectedKey() === menuItem.key}
                  onClick={() => handleSelect(menuItem.key)}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{menuItem.icon}</ListItemIcon>
                  <ListItemText primary={menuItem.label} />
                </ListItemButton>
              );
            }

            // Check role permissions for sections
            if (menuItem.section) {
              if (!hasPermission(menuItem.roles)) return null;
              
              // Filter items within the section based on role
              const visibleItems = menuItem.items.filter(item => hasPermission(item.roles));
              
              // Don't show section if no items are visible
              if (visibleItems.length === 0) return null;
              
              return (
                <Box key={menuItem.section}>
                  <ListItemButton
                    onClick={() => handleSectionToggle(menuItem.section)}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      mb: 0.5,
                      bgcolor: openSections[menuItem.section] ? 'action.hover' : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{menuItem.icon}</ListItemIcon>
                    <ListItemText 
                      primary={menuItem.label} 
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                    />
                    {openSections[menuItem.section] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={openSections[menuItem.section]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {visibleItems.map((item) => (
                        <ListItemButton
                          key={item.key}
                          selected={getSelectedKey() === item.key}
                          onClick={() => handleSelect(item.key)}
                          sx={{
                            pl: 4,
                            borderRadius: 2,
                            mx: 1,
                            mb: 0.5,
                            '&.Mui-selected': {
                              bgcolor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'white',
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                          <ListItemText primary={item.label} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            }
            return null;
          })}
        </List>
        <Box sx={{ p: 2, mt: 'auto' }}>
          <ListItem button onClick={handleLogout}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Brightness4Icon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">Dark mode</Typography>
            <Switch sx={{ ml: 'auto' }} />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
